// @-mention autocomplete for Trumbowyg editors.
//
// Wiring lives in `trumbowyg.js`, which calls `attachMentions(editorEl)` from
// the `tbwinit` event so every newly-initialized editor gets a Tribute instance.
//
// JSON shape returned by /mentions.json:
//   [{type, id, name, subtitle, url}, ...]
//
// Inserted HTML in the editor (saved as-is to the DB description column):
//   <a class="mention" data-mention-type="character" data-mention-id="42"
//      href="/campaigns/X/characters/42">@Tessara</a>
//
// The server-side `render_mentions` helper rewrites these on display with the
// current name/href, so renames propagate instantly.

import Tribute from "tributejs"

const FETCH_DEBOUNCE_MS = 150

// Build campaign-scoped URLs from the current page's path. Each browser tab
// has its own URL, so this is reliable across multi-tab scenarios where a GM
// has different campaigns open simultaneously. Session-based campaign context
// would silently cross-contaminate between tabs.
function mentionsIndexUrl(campaignId) {
  return `/campaigns/${campaignId}/mentions.json`
}
function mentionsHydrateUrl(campaignId) {
  return `/campaigns/${campaignId}/mentions/hydrate.json`
}

// HTML-escape user-controlled strings before interpolating into templates.
function esc(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]))
}

function typeBadge(type) {
  // Single-letter type indicator in the menu (C / P / Q / K).
  const letter = (type || "?").charAt(0).toUpperCase()
  return `<span class="mention-menu-type mention-menu-type--${esc(type)}">${esc(letter)}</span>`
}

// Eye-evil icon used app-wide to denote pc_secret records. Inlined here for
// the autocomplete menu and editor hydration; the display-side render uses
// CampaignsHelper#secret_icon for parity.
const SECRET_ICON_HTML =
  '<i class="fad fa-eye-evil fa-sm fa-fw" title="This is secret. Only GMs can see it."></i>'

function menuItemTemplate(item) {
  const v = item.original
  const subtitle = v.subtitle ? `<small class="mention-menu-subtitle">${esc(v.subtitle)}</small>` : ""
  const secret = v.secret ? ` ${SECRET_ICON_HTML}` : ""
  return `${typeBadge(v.type)}<span class="mention-menu-name">${esc(v.name)}${secret}</span>${subtitle}`
}

function selectTemplate(item) {
  // Guard for the "no match" path where Tribute can call this without an item.
  if (!item || !item.original) return ""
  const v = item.original
  return (
    `<a class="mention" data-mention-type="${esc(v.type)}" ` +
    `data-mention-id="${esc(v.id)}" href="${esc(v.url)}">@${esc(v.name)}</a>&nbsp;`
  )
}

// Module-level fetch coordinator so rapid typing only keeps the latest request.
let inFlight = null

function fetchValues(query, cb) {
  // Resolve the campaign from this tab's URL — not session — so multi-tab GMs
  // never see results from the wrong campaign. If the editor isn't on a
  // campaign-scoped page (rare), there's nothing meaningful to suggest.
  const campaignId = campaignIdFromPath(window.location.pathname)
  if (!campaignId) {
    cb([])
    return
  }

  if (inFlight) inFlight.abort()
  const ctrl = new AbortController()
  inFlight = ctrl

  // Debounce by deferring with a small timeout; abort if a newer request comes in.
  const timer = setTimeout(() => {
    fetch(`${mentionsIndexUrl(campaignId)}?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => cb(Array.isArray(data) ? data : []))
      .catch((err) => {
        if (err.name === "AbortError") return
        // Keep failures silent in the UI; surface them in console for debugging.
        console.warn("[mentions] fetch failed:", err)
        cb([])
      })
  }, FETCH_DEBOUNCE_MS)

  ctrl.signal.addEventListener("abort", () => clearTimeout(timer))
}

function buildTribute() {
  return new Tribute({
    trigger: "@",
    menuItemLimit: 32,
    lookup: "name",
    fillAttr: "name",
    requireLeadingSpace: true,
    spaceSelectsMatch: false,
    values: fetchValues,
    menuItemTemplate,
    selectTemplate,
    noMatchTemplate: () => `<li class="tribute-no-match">No matches</li>`,
  })
}

// Extracts the campaign id from a path like "/campaigns/42/..." — returns null
// if the path doesn't include one. Used to detect when an anchor's href points
// to a different campaign than the current page.
function campaignIdFromPath(path) {
  const match = (path || "").match(/\/campaigns\/(\d+)/)
  return match ? match[1] : null
}

// Applies the cross-campaign warning style + Bootstrap tooltip to an anchor.
// Bootstrap 5 tooltips need explicit initialization for dynamically-inserted
// elements; `container: 'body'` is required because the anchor lives inside
// Trumbowyg's overflow context (the tooltip would otherwise be clipped).
const FOREIGN_TOOLTIP_TEXT =
  "This mention points to a different campaign. Re-tag it to link to an object in this campaign."

function markAnchorForeign(a) {
  a.classList.add("mention--foreign")
  a.setAttribute("title", FOREIGN_TOOLTIP_TEXT) // native fallback
  a.setAttribute("data-bs-toggle", "tooltip")
  a.setAttribute("data-bs-placement", "top")
  a.setAttribute("data-bs-container", "body")

  if (window.bootstrap && window.bootstrap.Tooltip) {
    try {
      new window.bootstrap.Tooltip(a, {
        title: FOREIGN_TOOLTIP_TEXT,
        placement: "top",
        container: "body",
      })
    } catch (err) {
      // If Bootstrap rejects the init for any reason, the `title` attribute
      // still provides a native browser tooltip as a fallback.
      console.warn("[mentions] tooltip init failed:", err)
    }
  }
}

// Live-resolve existing mentions inside the editor on load. Walks the editor
// content for `a.mention[data-mention-type][data-mention-id]` anchors, fetches
// current names + URLs from the hydrate endpoint, and updates each anchor in
// place so the editor view matches what `render_mentions` shows on display.
//
// For each anchor the server didn't return, we check the existing href: if it
// points to a different /campaigns/X/ than the current page, the mention was
// most likely pasted in from another campaign. We mark those anchors with a
// `.mention--foreign` class + tooltip so the editor can communicate "this
// link belongs to a different campaign — re-tag to fix." Display is unaffected
// because render_mentions still falls back to plain prose for cross-campaign
// refs (the lookup is campaign-scoped, so they read as "missing").
//
// Hidden-from-viewer and truly-deleted entities are left as snapshot text with
// no foreign marker — same as before.
//
// DOM-only updates: we don't dispatch synthetic input events, so Trumbowyg's
// undo history and dirty-tracking stay clean.
function hydrateMentionsInEditor(editorEl) {
  const anchors = editorEl.querySelectorAll(
    "a.mention[data-mention-type][data-mention-id]"
  )
  if (anchors.length === 0) return

  const seen = new Set()
  const refs = []
  anchors.forEach((a) => {
    const type = a.dataset.mentionType
    const id = a.dataset.mentionId
    if (!type || !id) return
    const key = `${type}:${id}`
    if (seen.has(key)) return
    seen.add(key)
    refs.push(key)
  })
  if (refs.length === 0) return

  const currentCampaignId = campaignIdFromPath(window.location.pathname)
  if (!currentCampaignId) return

  fetch(`${mentionsHydrateUrl(currentCampaignId)}?refs=${encodeURIComponent(refs.join(","))}`, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  })
    .then((r) => (r.ok ? r.json() : []))
    .then((data) => {
      const map = new Map()
      if (Array.isArray(data)) {
        data.forEach((item) => map.set(`${item.type}:${item.id}`, item))
      }
      anchors.forEach((a) => {
        const key = `${a.dataset.mentionType}:${a.dataset.mentionId}`
        const item = map.get(key)
        if (item) {
          // innerHTML rather than textContent so the secret icon renders.
          // Name must be escaped; the icon HTML is a static constant.
          const namePart = `@${esc(item.name)}`
          a.innerHTML = item.secret ? `${namePart} ${SECRET_ICON_HTML}` : namePart
          a.setAttribute("href", item.url)
          return
        }
        // No hydrate match — could be cross-campaign (paste), hidden from
        // viewer, or deleted. Only the cross-campaign case is detectable here
        // and useful to flag.
        if (currentCampaignId) {
          const anchorCampaignId = campaignIdFromPath(a.getAttribute("href"))
          if (anchorCampaignId && anchorCampaignId !== currentCampaignId) {
            markAnchorForeign(a)
          }
        }
      })
    })
    .catch((err) => {
      // Hydration failures are non-fatal — the editor still works with the
      // snapshot text. Surface to console for debugging only.
      console.warn("[mentions] hydrate failed:", err)
    })
}

// Attach Tribute to a single Trumbowyg-managed editable element.
// Idempotent: a second call on the same element is a no-op.
export function attachMentions(editorEl) {
  if (!editorEl || editorEl.dataset.mentionsAttached === "true") return
  editorEl.dataset.mentionsAttached = "true"
  try {
    const tribute = buildTribute()
    tribute.attach(editorEl)
    hydrateMentionsInEditor(editorEl)
  } catch (err) {
    // If Tribute fails to attach for any reason, log and leave the editor working
    // normally without mention autocomplete. Never break the editor itself.
    console.error("[mentions] failed to attach Tribute:", err)
    delete editorEl.dataset.mentionsAttached
  }
}

// Make available as a global for the jQuery-based init in trumbowyg.js.
window.attachMentions = attachMentions
